import { useCallback, useEffect } from 'react'

interface VehicleFormData {
  [key: string]: any
}

interface UseVehicleFormSessionReturn {
  saveFormData: (data: VehicleFormData) => void
  clearFormData: () => void
  hasUserData: boolean
  restoreFormData: (setValue: any) => void
}

const FORM_DATA_KEY = 'vehicle_form_data'
const CLEANUP_DELAY = 12 * 60 * 60 * 1000 // 12 hours


const cleanupExpiredData = (storageKey: string) => {
  try {
    const timestampKey = `${storageKey}_timestamp`
    const savedTimestamp = sessionStorage.getItem(timestampKey)
    
    if (savedTimestamp) {
      const saveTime = parseInt(savedTimestamp, 10)
      const now = Date.now()
      const timeElapsed = now - saveTime
      
      if (timeElapsed >= CLEANUP_DELAY) {
        // Remove both data and timestamp keys
        sessionStorage.removeItem(storageKey)
        sessionStorage.removeItem(timestampKey)
        
        // Verify cleanup
        const dataRemoved = !sessionStorage.getItem(storageKey)
        const timestampRemoved = !sessionStorage.getItem(timestampKey)
        
        return true // Data was cleaned up
      }
    } else {
      const dataExists = !!sessionStorage.getItem(storageKey)
      if (dataExists) {
        sessionStorage.removeItem(storageKey)
        return true
      }
    }
    return false // No cleanup needed
  } catch (error) {
    console.error('Cleanup check failed:', error)
    return false
  }
}

// Helper function to save with timestamp
const saveWithTimestamp = (storageKey: string, data: any) => {
  try {
    const now = Date.now()
    sessionStorage.setItem(storageKey, JSON.stringify(data))
    sessionStorage.setItem(`${storageKey}_timestamp`, now.toString())
  } catch (error) {
    console.error('Save with timestamp failed:', error)
  }
}

export const useVehicleFormSession = (
  inventoryCollection: 'master_inventory' | 'shield_inventory',
  isFormOpen: boolean,
  isNewVehicle: boolean,
  vehicleId?: string
): UseVehicleFormSessionReturn => {
  
  // Generate storage key
  const storageKey = isNewVehicle 
    ? `${FORM_DATA_KEY}_${inventoryCollection}_ADD_NEW`
    : `${FORM_DATA_KEY}_${inventoryCollection}_EDIT_${vehicleId}`

  // Check for expired data on every form interaction (ONLY for this specific form)
  useEffect(() => {
    if (isFormOpen) {
      const hasExistingData = !!sessionStorage.getItem(storageKey)
      if (hasExistingData) {
        // First check if it's expired
        const wasCleanedUp = cleanupExpiredData(storageKey)
        if (wasCleanedUp) {
          return
        }
        
        // If not expired, ensure timestamp exists
        const timestampKey = `${storageKey}_timestamp`
        const hasTimestamp = !!sessionStorage.getItem(timestampKey)
        
        if (!hasTimestamp) {
          sessionStorage.setItem(timestampKey, Date.now().toString())
        }
      } else {
        // Clean up any orphaned timestamp keys
        const timestampKey = `${storageKey}_timestamp`
        const hasOrphanedTimestamp = !!sessionStorage.getItem(timestampKey)
        if (hasOrphanedTimestamp) {
          sessionStorage.removeItem(timestampKey)
        }
      }
    }
  }, [isFormOpen, storageKey])

  useEffect(() => {
    if (!isFormOpen) return
    
    const cleanupInterval = setInterval(() => {
      const wasCleanedUp = cleanupExpiredData(storageKey)
    }, 30000) 

    return () => {
      clearInterval(cleanupInterval)
    }
  }, [isFormOpen, storageKey])

  const saveFormData = useCallback((data: VehicleFormData) => {
    if (!isFormOpen) return
    try {
      if (isNewVehicle) {
        const { stock_number, ...dataToSave } = data
        saveWithTimestamp(storageKey, dataToSave)
      } else {
        saveWithTimestamp(storageKey, data)
      }
    } catch (error) {
      console.error('Save failed:', error)
    }
  }, [isFormOpen, isNewVehicle, storageKey])

  const restoreFormData = useCallback((setValue: any) => {
    if (!isFormOpen) return
    try {
      // First check if data is expired
      const wasCleanedUp = cleanupExpiredData(storageKey)
      if (wasCleanedUp) {
        return
      }

      const savedData = sessionStorage.getItem(storageKey)
      if (savedData) {
        const parsedData = JSON.parse(savedData)
        
        setTimeout(() => {
          Object.entries(parsedData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              setValue(key, value, { shouldValidate: false })
            }
          })
        }, 300)
        
        // Ensure timestamp exists for cleanup timer
        const timestampKey = `${storageKey}_timestamp`
        const hasTimestamp = !!sessionStorage.getItem(timestampKey)
        
        if (!hasTimestamp) {
          sessionStorage.setItem(timestampKey, Date.now().toString())
        } else {
          // Refresh the timestamp since we're actively using the data
          sessionStorage.setItem(timestampKey, Date.now().toString())
        }
      } else {
      }
    } catch (error) {
      console.error('Restore failed:', error)
    }
  }, [isFormOpen, storageKey])

  const clearFormData = useCallback(() => {
    const timestampKey = `${storageKey}_timestamp`
    
    // Show what exists before clearing
    const existsBefore = !!sessionStorage.getItem(storageKey)
    const timestampExistsBefore = !!sessionStorage.getItem(timestampKey)
    
    try {
      sessionStorage.removeItem(storageKey)
      sessionStorage.removeItem(timestampKey)
    } catch (error) {
      console.error('Clear failed:', error)
    }
  }, [storageKey])

  const hasUserData = useCallback(() => {
    const savedData = sessionStorage.getItem(storageKey)
    const hasData = !!savedData && savedData !== '{}' && savedData !== 'null'
    return hasData
  }, [storageKey])

  return {
    saveFormData,
    clearFormData,
    hasUserData: hasUserData(),
    restoreFormData
  }
}